import type { QueryClient } from "@tanstack/react-query"
import * as adsApi from "@/api/endpoints/adsCampaign"
import { confirmSubscriptionPayment } from "@/api/endpoints/business/business-plan"
import { businessKeys } from "@/queries/business/business"
import { businessPlanKeys } from "@/queries/business/business-plan"

const PAID_STATUSES = new Set(["COMPLETED", "SUCCESSFUL"])

type ConfirmPaymentResponse = {
  paystack?: { status?: string }
  business?: { subscriptionStatus?: string }
}

export function isPaystackPaymentConfirmed(response: unknown): boolean {
  const status = String(
    (response as ConfirmPaymentResponse)?.paystack?.status ?? ""
  ).toUpperCase()
  return PAID_STATUSES.has(status)
}

export function isSubscriptionPaymentConfirmed(response: unknown): boolean {
  if (isPaystackPaymentConfirmed(response)) return true
  return (
    (response as ConfirmPaymentResponse)?.business?.subscriptionStatus === "ACTIVE"
  )
}

export async function tryConfirmPaystackPayment(
  reference: string
): Promise<boolean> {
  const response = await adsApi.confirmAdCampaignPayment(reference)
  return isPaystackPaymentConfirmed(response)
}

export async function tryConfirmSubscriptionPayment(
  reference: string
): Promise<boolean> {
  try {
    const response = await confirmSubscriptionPayment(reference)
    return isSubscriptionPaymentConfirmed(response)
  } catch {
    return false
  }
}

function uniqueReferences(references: Array<string | undefined>): string[] {
  return Array.from(new Set(references.filter((ref): ref is string => !!ref)))
}

export function pollPaymentConfirmation(options: {
  references: Array<string | undefined>
  confirm: (reference: string) => Promise<boolean>
  onConfirmed: () => void
  onPending?: () => void
  intervalMs?: number
  maxAttempts?: number
}): () => void {
  const references = uniqueReferences(options.references)
  const {
    confirm,
    onConfirmed,
    onPending,
    intervalMs = 3000,
    maxAttempts = 40,
  } = options

  if (references.length === 0) {
    return () => {}
  }

  let attempts = 0
  let stopped = false
  let timer: ReturnType<typeof setInterval> | null = null

  const stop = () => {
    stopped = true
    if (timer) {
      clearInterval(timer)
      timer = null
    }
  }

  const attemptConfirm = async (): Promise<boolean> => {
    for (const reference of references) {
      try {
        if (await confirm(reference)) {
          return true
        }
      } catch {
        /* keep trying other references / poll again */
      }
    }
    return false
  }

  void (async () => {
    if (stopped) return
    if (await attemptConfirm()) {
      if (!stopped) onConfirmed()
      return
    }

    timer = setInterval(async () => {
      if (stopped) return

      attempts += 1
      if (await attemptConfirm()) {
        stop()
        if (!stopped) onConfirmed()
        return
      }

      if (attempts >= maxAttempts) {
        stop()
        if (!stopped) onPending?.()
      }
    }, intervalMs)
  })()

  return stop
}

export function pollPaystackPaymentConfirmation(options: {
  references: Array<string | undefined>
  onConfirmed: () => void
  onPending?: () => void
  intervalMs?: number
  maxAttempts?: number
}): () => void {
  return pollPaymentConfirmation({
    ...options,
    confirm: tryConfirmPaystackPayment,
  })
}

export function pollSubscriptionPaymentConfirmation(options: {
  references: Array<string | undefined>
  onConfirmed: () => void
  onPending?: () => void
  intervalMs?: number
  maxAttempts?: number
}): () => void {
  return pollPaymentConfirmation({
    ...options,
    confirm: tryConfirmSubscriptionPayment,
  })
}

export function invalidateSubscriptionQueries(
  queryClient: QueryClient,
  businessId: string
) {
  queryClient.invalidateQueries({ queryKey: businessKeys.detail(businessId) })
  queryClient.invalidateQueries({
    queryKey: businessPlanKeys.subscription(businessId),
  })
  queryClient.invalidateQueries({ queryKey: businessKeys.dashboard(businessId) })
}
